'use client';

import React, { useState, useEffect } from 'react';
import { Heart, Scale } from 'lucide-react';
import { listingsPath } from '@/lib/results-paging';
import { useRouter, useSearchParams } from 'next/navigation';
import Transition from '@/components/ui/Transition';
import { getFavoritesServer, syncFavoritesServer } from '@/app/actions/property';
import { notify } from '@/lib/dialogs';
import { getFavoriteIds, setFavoriteIds, useFavoriteIds, useCompareIds } from '@/lib/local-lists';
import { comparePath } from '@/lib/localized-paths';

interface NavbarControlsProps {
  lang: string;
}

export default function NavbarControls({ lang }: NavbarControlsProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  
  // The counts come from the store. What is left in an effect is the part that
  // genuinely belongs in one: talking to the server.
  const favoriteIds = useFavoriteIds();
  const compareIds = useCompareIds();
  const favoriteCount = favoriteIds.length;
  const compareCount = compareIds.length;
  const [] = useState(false);

  // Derived from the URL rather than mirrored into state by an effect: the URL
  // is already the source of truth and the copy could only ever lag it.
  const favoritesOnly = searchParams?.get('favorites') === 'true';

  const isFr = lang === 'fr';

  useEffect(() => {
    // Merge whatever the account has with whatever this device has, once.
    const initFavorites = async () => {
      try {
        const res = await getFavoritesServer();
        if (res.success && res.favorites) {
          const localFavs = getFavoriteIds();
          const serverFavs = Array.isArray(res.favorites) ? res.favorites : [];
          const merged = Array.from(new Set([...localFavs, ...serverFavs]));
          if (merged.length > 0) setFavoriteIds(merged);
        }
      } catch (e) {
        console.error('Failed to init favorites', e);
      }
    };
    initFavorites();
  }, []);

  // Push the list to the account whenever it changes, debounced. This used to
  // be buried inside the counter update, so it only ran when a count happened
  // to be recalculated.
  useEffect(() => {
    const timer = setTimeout(() => {
      syncFavoritesServer(favoriteIds).catch(() => {});
    }, 500);
    return () => clearTimeout(timer);
  }, [favoriteIds]);

  const toggleFavoritesFilter = () => {
    const favArray = getFavoriteIds();

    const params = new URLSearchParams(searchParams?.toString() || '');
    if (favoritesOnly) {
      params.delete('favorites');
      params.delete('ids');
      router.push(`${listingsPath(lang)}?${params.toString()}`);
    } else {
      if (favArray.length === 0) {
        notify(isFr ? 'Vous n\'avez pas encore de favoris.' : 'You do not have any favorites yet.', 'info');
        return;
      }
      params.set('favorites', 'true');
      params.set('ids', favArray.join(','));
      router.push(`${listingsPath(lang)}?${params.toString()}`);
    }
  };

  const handleCompareClick = () => {
    if (compareIds.length > 0) {
      router.push(comparePath(lang, compareIds));
    } else {
      router.push(comparePath(lang));
    }
  };

  return (
    // Favourites and compare live in the bottom bar on a phone. Showing
    // them here as well put the same three actions on screen twice, forty
    // pixels apart, and two identical controls usually mean two different
    // outcomes. Below md they belong to the bottom bar alone; from md up
    // there is no bottom bar, so they are the only route.
    <div className="hidden md:flex items-center gap-2 sm:gap-3" id="navbar-controls-root">
      {/* Favorites Toggle */}
      <button
        onClick={toggleFavoritesFilter}
        aria-label={isFr ? 'Favoris' : 'Favorites'}
        title={isFr ? 'Voir mes favoris' : 'View my favorites'}
        className={`relative flex items-center justify-center w-11 h-11 rounded-full text-xs font-semibold transition cursor-pointer border hover:scale-105 active:scale-95 ${
          favoritesOnly
            ? 'text-danger bg-danger/10 border-danger/30'
            : 'bg-neutral-900 text-neutral-300 border-neutral-800 hover:bg-neutral-800'
        }`}
      >
        <Heart className={`w-4 h-4 ${favoritesOnly ? 'fill-current text-danger' : ''}`} />
            <Transition
              as="span"
              show={favoriteCount > 0}
              from={{ scale: 0, opacity: 0 }}
              duration={250}
              easing="spring"
              className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-mono font-bold rounded-full bg-danger text-black shadow-md border border-neutral-950"
            >
              {favoriteCount}
            </Transition>
      </button>

      {/* Compare Button */}
      <button
        onClick={handleCompareClick}
        aria-label={isFr ? 'Comparer' : 'Compare'}
        title={isFr ? 'Comparer les annonces sélectionnées' : 'Compare selected properties'}
        className="relative flex items-center justify-center w-11 h-11 rounded-full text-xs font-semibold border bg-neutral-900 text-neutral-300 border-neutral-800 hover:bg-neutral-800 cursor-pointer transition hover:scale-105 active:scale-95"
      >
        <Scale className="w-4 h-4 text-champagne" />
            <Transition
              as="span"
              show={compareCount > 0}
              from={{ scale: 0, opacity: 0 }}
              duration={250}
              easing="spring"
              className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-mono font-bold rounded-full bg-champagne text-black shadow-md border border-neutral-950"
            >
              {compareCount}
            </Transition>
      </button>
    </div>
  );
}
